import { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Alert from 'reactstrap/lib/Alert';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import { Tooltip, Typography } from '@mui/material';

import Card from 'Lib/components/common/Card';
import DateTime from 'Lib/components/common/DateTime';
import Form from 'Lib/components/form/Form';
import IdentityReportStatuses from 'Lib/constants/screening/identityReportStatuses';

import DocumentImages from './components/DocumentImages';

const alertStyle = { marginLeft: '10px', marginRight: '10px' };

const ResendModal = ({ isOpen, onCancel, onSubmit }) => {
	return (
		<Form
			render={form => {
				return (
					<Modal isOpen={isOpen}>
						<ModalHeader>Resend Verification to Applicant</ModalHeader>
						<ModalBody>
							This will resend the identity verification form to the Applicant using the email provided in the application. You will be billed $5.00 for the new
							verification report.
						</ModalBody>
						<ModalFooter>
							<Button onClick={onCancel} outline>
								Cancel
							</Button>
							<Button onClick={() => onSubmit('', form)} color="primary" disabled={form.isSubmitting}>
								{form.isSubmitting ? 'Sending' : 'Send'}
							</Button>
						</ModalFooter>
					</Modal>
				);
			}}
		/>
	);
};

const renderStatus = (statusID, verificationActive, onOpenResendModal) => {
	switch (parseInt(statusID, 10)) {
		case IdentityReportStatuses.Failed:
			return (
				<Alert color="danger" style={alertStyle}>
					The Identity Report has failed. Rentvine support has been notified and will look into this issue.
				</Alert>
			);
		case IdentityReportStatuses.Skipped:
			return (
				<Alert color="warning" style={alertStyle}>
					<Typography mb={2}>
						This applicant has opted-out or was unable to complete the Identity Verification. Manually uploaded documents are shown below if required by the application
						template.
					</Typography>
					<Button onClick={onOpenResendModal} outline color="primary">
						Resend Verification
					</Button>
				</Alert>
			);
		case IdentityReportStatuses.NotEnabled:
			return (
				<Alert color="warning" style={alertStyle}>
					{verificationActive
						? 'Identity Verification was not enabled when this application was submitted.'
						: 'Identity Verification is not enabled for this application template. To enable, turn on Identity Verification in the template builder.'}
				</Alert>
			);
		default:
			return null;
	}
};

const Verification = ({ data }) => {
	const { matchName, matchBirthDate, matchCountry, notExpired, biometricCheck } = data;

	const VerificationItem = ({ label, state }) => {
		const iconStyles = { marginRight: '12px' };

		const icons = {
			passed: <i className="fa fa-check" style={{ ...iconStyles, color: 'green' }}></i>,
			failed: <i className="fa fa-times" style={{ ...iconStyles, color: '#c80303' }}></i>,
			intermediate: <i className="fa fa-exclamation-triangle" style={{ ...iconStyles, color: '#dba810' }}></i>
		};
		return (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				{icons[state]}
				<p style={{ margin: 0 }}>{label}</p>
			</div>
		);
	};

	return (
		<div>
			<dt>
				Biometric Check{' '}
				<Tooltip title="Biometric analysis of an Applicant's selfie is used to verify that the Applicant is a real person and that they match the photo shown in their uploaded document">
					<i className="fa fa-question-circle-o" aria-hidden="true" style={{ color: '#a2a2a2' }}></i>
				</Tooltip>
			</dt>
			<dd>
				{biometricCheck === 'success' ? (
					<VerificationItem label="Applicant selfie matches document photo" state="passed" />
				) : (
					<VerificationItem label="Applicant selfie did not match the document photo" state="failed" />
				)}
			</dd>
			<dt>Name</dt>
			<dd>
				{matchName === 'match' ? (
					<VerificationItem label="Name provided matches document name" state="passed" />
				) : matchName === 'partial_match' ? (
					<VerificationItem label="Name provided partially matches document name" state="intermediate" />
				) : (
					<VerificationItem label="Name provided does not match document name" state="failed" />
				)}
			</dd>
			<dt>Date of Birth</dt>
			<dd>
				{matchBirthDate === 'match' ? (
					<VerificationItem label="Date of birth provided matches document date of birth" state="passed" />
				) : matchName === 'partial_match' ? (
					<VerificationItem label="Date of birth provided partially matches document date of birth" state="intermediate" />
				) : (
					<VerificationItem label="Date of birth provided does not match document date of birth" state="failed" />
				)}
			</dd>
			<dt>Country</dt>
			<dd>
				{matchCountry === 'match' ? (
					<VerificationItem label="Country provided matches document issuance country" state="passed" />
				) : (
					<VerificationItem label="Country provided does not match document issuance country" state="failed" />
				)}
			</dd>
			<dt>Expiration</dt>
			<dd>
				{notExpired === 'not_expired' ? (
					<VerificationItem label="Document is not expired" state="passed" />
				) : matchName === 'no_data' ? (
					<VerificationItem label="Document expiration could not be determined" state="intermediate" />
				) : (
					<VerificationItem label="Document is expired" state="failed" />
				)}
			</dd>
		</div>
	);
};

const DocumentData = ({ data }) => {
	const { documentType, idNumber, expirationDate } = data;

	return (
		<div>
			<dt>Document Type</dt>
			<dd>{documentType}</dd>
			<dt>Document Number</dt>
			<dd>{idNumber}</dd>
			<dt>Document Expiration</dt>
			<dd>{expirationDate}</dd>
		</div>
	);
};

const ReportProcessing = () => (
	<div className="text-center">
		<i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }} />
		Report is still processing, please check again in a few minutes
	</div>
);

const AwaitingApplicant = ({ created }) => {
	return (
		<div>
			<p>You have requested that the applicant resubmit identity verification</p>
			<dt>Date Requested</dt>
			<dd>
				<DateTime value={created} />
			</dd>
			<dt>Status</dt>
			<dd>Awaiting applicant response</dd>
		</div>
	);
};

const ResendForCompleted = ({ onClicked }) => {
	return (
		<div style={{ ...alertStyle, fontSize: '14px', marginBottom: '1rem' }}>
			<p>Have an issue with the submitted identity verification? You can resend the verification to have the applicant retry the process.</p>
			<Button onClick={onClicked} outline color="secondary">
				Resend Verification
			</Button>
		</div>
	);
};

const IdentityPage = ({ verificationData, onResubmit }) => {
	const { statusID, verificationActive, identity: { verification, data, errors, createdDateTime } = {}, files } = verificationData;
	const [resendModalOpen, setResendModalOpen] = useState(false);

	const processing = statusID == IdentityReportStatuses.Processing;
	const completed = statusID == IdentityReportStatuses.Completed;
	const resent = statusID == IdentityReportStatuses.Resent;

	const handleResend = (message, form) => {
		onResubmit({ message: message }, form).then(() => {
			setResendModalOpen(false);
		});
	};

	return (
		<>
			<ResendModal isOpen={resendModalOpen} onCancel={() => setResendModalOpen(false)} onSubmit={handleResend} />
			{renderStatus(statusID, verificationActive, () => {
				setResendModalOpen(true);
			})}
			{errors && errors.length && (
				// Errors from the provider
				<Alert color="danger" style={alertStyle}>
					The Identity Report was not completed because {errors[0]}
				</Alert>
			)}
			<Row>
				<Col lg="5">
					{resent && (
						<Card title="Resent Verification">
							<AwaitingApplicant created={createdDateTime} />
						</Card>
					)}
					<Card title="Verification">{processing ? <ReportProcessing /> : completed && <Verification data={verification} />}</Card>
					<Card title="Document Data">{processing ? <ReportProcessing /> : completed && <DocumentData data={data} />}</Card>
					{completed && <ResendForCompleted onClicked={() => setResendModalOpen(true)} />}
				</Col>
				<Col lg="7">
					<Card title="Documents">{files && <DocumentImages files={files} />}</Card>
				</Col>
			</Row>
		</>
	);
};

export default IdentityPage;
